import styled from 'styled-components';
import { CrossIcon } from '../icons';
import { addAlpha } from '@algoan/deep-dive/theme';

export enum FilterVariant {
  PRIMARY = 'primary',
  LEGACY = 'legacy',
}

export enum FilterBottomMargin {
  SMALL = '4px',
  LARGE = '8px',
}

export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1;

  & > *:not(:last-child) {
    margin-right: 6px;
  }
`;

export interface FilterProps {
  withBottomMargin?: FilterBottomMargin;
  withLeftMargin?: boolean;
  selected?: boolean;
  color?: string;
  background?: string;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  onClickCross?: () => void;
  withCross?: boolean;
  className?: string;
  variant?: FilterVariant;
  border?: boolean;
}

export const Filter: React.FC<FilterProps> = ({
  withBottomMargin,
  selected,
  color,
  background,
  disabled,
  children,
  onClick,
  onClickCross,
  withLeftMargin,
  withCross,
  className,
  border,
  variant = FilterVariant.PRIMARY,
}) => {
  return (
    <S.FilterContainer
      onClick={onClick}
      withBottomMargin={withBottomMargin}
      withLeftMargin={withLeftMargin}
      selected={selected}
      color={color}
      background={background}
      disabled={disabled}
      className={className}
      shadowOnHover={!withCross}
      variant={variant}
      border={border}
    >
      <S.Label>{children}</S.Label>
      {withCross && (
        <S.CrossIconContainer data-testid="filter-cross" onClick={onClickCross}>
          <CrossIcon size="small" />
        </S.CrossIconContainer>
      )}
    </S.FilterContainer>
  );
};

const FilterContainer = styled.div<{
  variant: FilterVariant;
  withBottomMargin?: FilterBottomMargin;
  withLeftMargin?: boolean;
  selected?: boolean;
  color?: string;
  background?: string;
  disabled?: boolean;
  shadowOnHover?: boolean;
  border?: boolean;
}>`
  width: fit-content;
  display: flex;
  background: ${({ theme, selected, background, variant }) => {
    if (variant === FilterVariant.LEGACY)
      return selected ? addAlpha(theme.legacy.secondary, 0.75) : (background ?? 'white');
    return theme.secondary.chalk;
  }};
  border-radius: 15px;
  border: 1px solid
    ${({ selected, border, theme, color, variant }) => {
      if (variant === FilterVariant.LEGACY) return color ?? addAlpha(theme.legacy.secondary, 0.75);
      return border && selected ? theme.main.deepBlue : 'transparent';
    }};
  -webkit-background-clip: padding-box; /* transparency on the border does not work without this */
  background-clip: padding-box;
  box-sizing: border-box;
  color: ${({ selected, color, theme, variant }) => {
    if (variant === FilterVariant.LEGACY) return selected ? 'white' : (color ?? theme.legacy.secondary);
    return selected ? theme.main.deepBlue : (color ?? theme.secondary.softBlue4);
  }};
  cursor: ${(props) => (props.onClick ? 'pointer' : (props.color ?? 'default'))};
  font-size: 12px;
  line-height: 14px;
  ${(props) =>
    props.withBottomMargin
      ? `
        margin-bottom: ${props.withBottomMargin};
  `
      : ''}
  ${(props) =>
    props.withLeftMargin
      ? `
       margin-left: 8px;
      `
      : ''}
  padding: 4px 8px;
  ${(props) => (props.disabled ? 'pointer-events: none;' : '')}

  &:hover {
    ${(props) =>
      props.shadowOnHover ? `box-shadow: 0px 2px 13px 0px ${addAlpha(props.theme.algoanBlue, 0.15)};` : ''};
  }
`;

const S = {
  FilterContainer,
  Label: styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
    width: max-content;
  `,
  CrossIconContainer: styled.div`
    margin-left: 8px;
    cursor: pointer;

    ${FilterContainer}:hover &, ${FilterContainer}:focus-within & {
      & path {
        fill: ${(props) => props.theme.secondary.softBlue2};
      }
    }
  `,
};

export { S as FilterStyle };
